/**
 * SEO Sprint 2 (Feb 2026) — Destination landing page renderer.
 *
 * Eén component verzorgt alle 4 destination landingpages:
 *   /shipping-to-aruba
 *   /shipping-to-bonaire
 *   /shipping-to-sint-maarten
 *   /shipping-to-suriname
 *
 * Pagina-architectuur (van boven naar beneden):
 *   1. SEO meta (titel, description, keywords) — uit CMS sectie `seo`
 *   2. JSON-LD: BreadcrumbList + Service (zee + lucht naar bestemming)
 *   3. Hero PageBanner (re-use ToolPageBanner → identieke look-and-feel)
 *   4. Breadcrumbs UI (Home → Bestemmingen → Naam)
 *   5. Intro tekst-blok (eyebrow + H2 + body + veilige disclaimer)
 *   6. Service cards (4 vaste types: zee / lucht / pickup / documenten)
 *   7. FAQ-accordeon (uit CMS)
 *   8. CTA-band (offerte / bellen)
 *
 * Geen harde transittijden of garanties — alleen wat in de CMS staat.
 * Alle URL-CTA's wijzen veilig naar bestaande pagina's (offerte,
 * paklijst, contact, bellen).
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Ship, Plane, Package, Truck, Car, MapPin,
  Briefcase, User, CheckCircle2, Clock, ShieldCheck, Banknote,
  Headphones, Info, Receipt, Zap, ChevronDown,
} from "lucide-react";

import SEO from "./SEO";
import Schema from "./Schema";
import Breadcrumbs from "./Breadcrumbs";
import { COMPANY } from "./Schema";
import { ToolPageBanner, ToolPageCtaBand } from "./ToolPageCms";
import RelatedDestinations from "./RelatedDestinations";
import useDestinationCms from "../hooks/useDestinationCms";

const ICON_REGISTRY = {
  ship: Ship, plane: Plane, package: Package, truck: Truck, car: Car,
  map: MapPin, briefcase: Briefcase, user: User,
  check: CheckCircle2, clock: Clock, shield: ShieldCheck,
  banknote: Banknote, support: Headphones, sparkles: Sparkles,
  info: Info, receipt: Receipt, zap: Zap,
};
const resolveIcon = (k) => ICON_REGISTRY[String(k || "").toLowerCase()] || Package;

const CARD_GRADIENT = "from-[#082c50] via-[#1a5193] to-[#194d8d]";

const ORIGIN = (process.env.REACT_APP_PUBLIC_ORIGIN
  || "https://www.5999cargo.nl").replace(/\/$/, "");

export default function DestinationPage({ slug, fallback }) {
  const { data } = useDestinationCms(slug, fallback);
  const banner = data?.page_banner || {};
  const intro = data?.intro || {};
  const serviceItems = Array.isArray(data?.service_blocks?.items)
    ? data.service_blocks.items
    : [];
  const processSteps = (Array.isArray(data?.process_steps?.items) ? data.process_steps.items : [])
    .filter((it) => it && (it.title || it.description));
  const trustSignals = (Array.isArray(data?.trust_signals?.items) ? data.trust_signals.items : [])
    .filter((it) => it && (it.title || it.description));
  const faqItems = (Array.isArray(data?.faq?.items) ? data.faq.items : [])
    .filter((it) => it && it.question && it.answer);
  const cta = data?.cta_band || {};
  const seo = data?.seo || {};
  const displayName = data?.display_name || slug;
  const urlPath = data?.url_path || `/shipping-to-${slug}`;
  const countryCode = data?.country_code || "";

  // Schema.org Service — generic + safe; no prices, no transit-time claims
  const serviceSchemaData = {
    name: `Verzending naar ${displayName}`,
    description: (
      `Verzending van Nederland naar ${displayName} via zee- of luchtvracht. `
      + `Pickup, documenten en aflevering verzorgd door 5999Cargo Group Europe.`
    ),
    serviceType: "Cargo shipping",
    url: `${ORIGIN}${urlPath}`,
    // Tighten areaServed to the single destination country for this Service node
    areaServed: countryCode
      ? [{ type: "Country", name: displayName }]
      : COMPANY.areaServed,
  };

  // Breadcrumbs: Home → Bestemmingen (clickable to the destination index
  // section on the contact page, or to /hubs as the geographic overview)
  // → current destination (no URL → marks current page).
  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Bestemmingen", url: "/hubs" },
    { name: displayName },
  ];

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />
      <Schema type="Service" data={serviceSchemaData} />

      {/* HERO */}
      <ToolPageBanner
        banner={banner}
        eyebrowIcon={MapPin}
        testid={`destination-${slug}-banner`}
      />

      {/* BREADCRUMBS */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Breadcrumbs
            items={breadcrumbs}
            testid={`destination-${slug}-breadcrumbs`}
          />
        </div>
      </div>

      {/* INTRO */}
      {(intro.title || intro.body) && (
        <section
          className="bg-[#F8FAFC] py-14 sm:py-16"
          data-testid={`destination-${slug}-intro`}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {intro.eyebrow ? (
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
                {intro.eyebrow}
              </span>
            ) : null}
            {intro.title ? (
              <h2 className="font-chivo font-bold text-2xl sm:text-3xl lg:text-4xl text-[#082c50] tracking-tight mt-3 leading-tight">
                {intro.title}
              </h2>
            ) : null}
            {intro.body ? (
              <div className="prose prose-slate max-w-none mt-5 text-[#514e4e] leading-relaxed whitespace-pre-line">
                {intro.body}
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* SERVICE BLOCKS */}
      {serviceItems.length > 0 && (
        <section
          className="bg-white py-14 sm:py-16"
          data-testid={`destination-${slug}-services`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-10">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
                Onze diensten
              </span>
              <h2 className="font-chivo font-bold text-2xl sm:text-3xl text-[#082c50] tracking-tight mt-3">
                {`Wat wij voor uw zending naar ${displayName} regelen`}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceItems.map((c, i) => {
                const Icon = resolveIcon(c.icon);
                const Wrapper = c.link_url ? Link : "div";
                const wrapProps = c.link_url ? { to: c.link_url } : {};
                return (
                  <Wrapper
                    key={i}
                    {...wrapProps}
                    className={`group relative block h-full bg-white border border-slate-200 rounded-2xl p-6 ${c.link_url ? "hover:border-[#082c50]/40 hover:-translate-y-1 hover:shadow-[0_18px_38px_-22px_rgba(8,44,80,0.3)]" : ""} transition-all overflow-hidden`}
                    data-testid={`destination-${slug}-service-${i}`}
                  >
                    <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${CARD_GRADIENT} opacity-10 group-hover:opacity-20 transition-opacity`} />
                    <div className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${CARD_GRADIENT} text-white`}>
                      <Icon className="w-6 h-6" strokeWidth={2} />
                    </div>
                    <h3 className="relative font-bold text-lg text-[#082c50] mt-4 tracking-tight">
                      {c.title}
                    </h3>
                    {c.description ? (
                      <p className="relative text-sm text-[#514e4e] mt-1.5 leading-relaxed">
                        {c.description}
                      </p>
                    ) : null}
                    {c.link_url ? (
                      <div className="relative mt-4 flex items-center justify-between pt-3 border-t border-slate-100">
                        <span className="text-[11px] uppercase tracking-[0.16em] text-[#1a5193] font-bold">
                          {c.link_label || "Meer info"}
                        </span>
                        <ArrowRight className="w-4 h-4 text-[#1a5193] group-hover:translate-x-1 transition-transform" />
                      </div>
                    ) : null}
                  </Wrapper>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PROCESS STEPS (stappenplan) — Sprint 3 */}
      {processSteps.length > 0 && (
        <section
          className="bg-[#F8FAFC] border-t border-slate-100 py-14 sm:py-16"
          data-testid={`destination-${slug}-process`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-10">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
                Stappenplan
              </span>
              <h2 className="font-chivo font-bold text-2xl sm:text-3xl text-[#082c50] tracking-tight mt-3">
                {`In 5 stappen uw zending naar ${displayName}`}
              </h2>
              <p className="mt-3 text-[#514e4e] leading-relaxed text-sm sm:text-base">
                Zo verloopt uw zending van offerte tot lokale aflevering — wij begeleiden u door elke stap.
              </p>
            </div>
            <ol
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
              data-testid={`destination-${slug}-process-list`}
            >
              {processSteps.map((step, i) => {
                const Icon = resolveIcon(step.icon);
                return (
                  <li
                    key={i}
                    className="relative bg-white border border-slate-200 rounded-2xl p-5"
                    data-testid={`destination-${slug}-process-step-${i}`}
                  >
                    <span className="absolute -top-3 left-5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#082c50] text-white text-sm font-black">
                      {i + 1}
                    </span>
                    <Icon className="w-6 h-6 text-[#1a5193] mt-1" strokeWidth={2} aria-hidden="true" />
                    <h3 className="font-bold text-[#082c50] mt-3 text-base tracking-tight">
                      {step.title}
                    </h3>
                    {step.description ? (
                      <p className="text-sm text-[#514e4e] mt-1.5 leading-relaxed">
                        {step.description}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ol>
          </div>
        </section>
      )}

      {/* TRUST SIGNALS — Sprint 3 */}
      {trustSignals.length > 0 && (
        <section
          className="bg-white py-14 sm:py-16"
          data-testid={`destination-${slug}-trust`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-10">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
                Waarom 5999Cargo
              </span>
              <h2 className="font-chivo font-bold text-2xl sm:text-3xl text-[#082c50] tracking-tight mt-3">
                Persoonlijke begeleiding, complete service
              </h2>
            </div>
            <ul
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3"
              data-testid={`destination-${slug}-trust-list`}
            >
              {trustSignals.map((sig, i) => {
                const Icon = resolveIcon(sig.icon);
                return (
                  <li
                    key={i}
                    className="bg-slate-50 border border-slate-200 rounded-2xl p-5"
                    data-testid={`destination-${slug}-trust-item-${i}`}
                  >
                    <Icon className="w-6 h-6 text-[#1a5193]" strokeWidth={2} aria-hidden="true" />
                    <h3 className="font-bold text-[#082c50] mt-3 text-base tracking-tight">
                      {sig.title}
                    </h3>
                    {sig.description ? (
                      <p className="text-sm text-[#514e4e] mt-1.5 leading-relaxed">
                        {sig.description}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqItems.length > 0 && (
        <>
          {/* FAQPage schema voor dit destination-onderwerp (lokaal scope) */}
          <Schema type="FAQPage" data={{ items: faqItems }} />
          <section
            className="bg-[#F8FAFC] border-t border-slate-100 py-14 sm:py-16"
            data-testid={`destination-${slug}-faq`}
          >
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-8">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#1a5193] font-bold">
                  Veelgestelde vragen
                </span>
                <h2 className="font-chivo font-bold text-2xl sm:text-3xl text-[#082c50] tracking-tight mt-3">
                  {`Vragen over verzendingen naar ${displayName}`}
                </h2>
              </div>
              <ul className="space-y-3" data-testid={`destination-${slug}-faq-list`}>
                {faqItems.map((q, i) => (
                  <li
                    key={i}
                    className="bg-white border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <details className="group">
                      <summary className="flex items-center justify-between gap-4 p-4 sm:p-5 cursor-pointer list-none">
                        <span className="font-semibold text-[#082c50] text-sm sm:text-base pr-2">
                          {q.question}
                        </span>
                        <ChevronDown
                          className="w-5 h-5 text-[#1a5193] flex-shrink-0 transition-transform group-open:rotate-180"
                          aria-hidden="true"
                        />
                      </summary>
                      <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-sm text-[#514e4e] leading-relaxed">
                        {q.answer}
                      </div>
                    </details>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}

      {/* CTA BAND */}
      <ToolPageCtaBand
        cta={cta}
        testid={`destination-${slug}-cta-band`}
      />

      {/* SEO Sprint 3 — internal linking strip */}
      <RelatedDestinations
        excludeSlug={slug}
        title="Andere populaire bestemmingen"
        eyebrow="Bestemmingen"
        description={`Ook regelmatig verzonden vanuit Nederland — bekijk wat wij naar de overige Caribbean-bestemmingen verzorgen.`}
        anchorTemplate="Verzenden naar {name}"
        testid={`destination-${slug}-related`}
        variant="card"
      />
    </>
  );
}
